import React, {Component} from "react"
import Papa from "papaparse"
import moment from "moment"

import Button from "@irati/ui/src/Button"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import makeCSV from "@irati/utils/csv/makeCSV"
import adminTracking, {adminDashboardTracking} from "@irati-tracking/admin"
import authentication from "@irati/auth"
import teamService from "@irati/identity/services/teams"
import userService from "@irati/identity/services/users"
import {Team, User, TeamState} from "@irati/types"
import {ProgressLoader, DownloadButton} from "@irati/ui/src/ProgressDownload"
import teamNameHelper from "@shared/core/utils/team/teamName"

interface Props extends InjectIntl {
  useProgressLoader?: boolean
}

interface State {
  csvLoad: Promise<void>|null,
  loading: boolean,
  hasDownloaded: boolean,
  percent: number
}

const LIMIT = 20

class DownloadUsersExport extends Component<Props, State> {
  state: State = {
    csvLoad: null,
    loading: false,
    hasDownloaded: false,
    percent: 0
  }

  getUserList = (users: User[] = [], cursor?: string, timesRequested: number = 1): Promise<User[]> => {
    const params = {
      businessId: authentication.business().uuid,
      cursor,
      includeIntegrations: true
    }

    return userService.getUsersForBusiness(params, {fetchPolicy: "network-only"}).then(({results, cursor: newCursor, total}) => {
      this.setState({percent: (timesRequested * LIMIT * 100) / total})
      const newUsers = results
      const allUsers = users.concat(newUsers)
      if (newCursor) {
        return this.getUserList(allUsers, newCursor, timesRequested + 1)
      }
      return allUsers
    })
  }

  getRegion = (user: User, regions: Team[]) => {
    const {formatMessage} = this.props
    const regionName = teamNameHelper(regions.find(({uuid}) => user.teamIds.find((id) => id === uuid) === uuid))
    return regionName && formatMessage({id: regionName})
  }

  getStatus = (user: User) => {
    const {formatMessage} = this.props
    if(user?.isEnabled) {
      if (user?.isActivated) {
        return formatMessage({id: "admin.components.downloadUsersExport.status.activated"})
      }
      return formatMessage({id: "admin.components.downloadUsersExport.status.notActivated"})
    }
    return formatMessage({id: "admin.components.downloadUsersExport.status.deactivated"})
  }

  getIntegrationStatus = (user: User) => {
    const {formatMessage} = this.props
    if (user?.directorySyncId) {
      return formatMessage({id: "admin.components.downloadUsersExport.integrationStatus.active"})
    }
    return formatMessage({id: "admin.components.downloadUsersExport.integrationStatus.inactive"})
  }

  handleCSVDownload = () => {
    const {formatMessage} = this.props

    adminDashboardTracking.downloadPeopleList()
    this.setState({loading: true})

    const csvLoad = teamService
      .list(authentication.business().uuid, {state: TeamState.Generic}, {fetchPolicy: "network-only"})
      .then((teams) => {
        return teamService
          .list(authentication.business().uuid, {state: TeamState.BillingUnit}, {fetchPolicy: "network-only"})
          .then((regions) => {
            return this.getUserList().then((users: User[]) => {
              const headers = [
                formatMessage({id: "admin.components.downloadUsersExport.headers.email"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.firstname"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.lastname"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.externalID"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.region"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.teams"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.status"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.dateAdded"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.numberOfDaysAdded"}),
                formatMessage({id: "admin.components.downloadUsersExport.headers.integrations"})
              ]
              const data = users.map((user: User) => [
                user.email,
                user.firstName,
                user?.lastName,
                user?.externalUserId,
                this.getRegion(user, regions),
                user?.teamIds.map((teamId) => teams.find((t) => t.uuid === teamId)?.teamName).filter((t) => !!t).join(", "),
                this.getStatus(user),
                moment(user.metadata.createdAt).format("DD/MM/YYYY"),
                moment().diff(moment(user.metadata.createdAt, "YYYY-MM-DD"), "days"),
                this.getIntegrationStatus(user)
              ])
              const csv = Papa.unparse({fields: headers, data})
              makeCSV(csv, "users.csv")
              adminTracking.userExport()

              this.setState({hasDownloaded: true, percent: 0})
              setTimeout(() => {
                this.setState({hasDownloaded: false, loading: false})
              }, 3000)
            })
          })
      })
    this.setState({csvLoad})
  }

  render() {
    const {csvLoad, loading, hasDownloaded, percent} = this.state
    const {useProgressLoader} = this.props

    if (useProgressLoader) {
      if (loading) {
        return <ProgressLoader hasDownloaded={hasDownloaded} percent={percent} />
      }
      return <DownloadButton
        id="adminSection__activationRate_downloadPeopleListId_button"
        onClick={this.handleCSVDownload}
        text="admin.components.downloadUsersExport.button" />
    }

    return (
      <Button primary onClick={this.handleCSVDownload} load={csvLoad}>
        <FormattedMessage id="admin.components.downloadUsersExport.button" />
      </Button>
    )
  }
}

export default injectIntl(DownloadUsersExport)
